import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { farmerAPI, productAPI, statsAPI } from '../../api/api';
import useIsMobile from '../../hooks/useIsMobile';
import { useApi } from '../../api/useApi';
import { isRiceProduct } from '../../utils/productCatalog';

function AnimatedCounter({ target, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 2000;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>
  );
}

const concepts = [
  {
    icon: 'No',
    title: 'Zero Middlemen',
    desc: 'We connect you directly to the farmer. No agents, no brokers, no hidden markups.',
    color: '#fee2e2',
    iconBg: '#ef4444',
  },
  {
    icon: 'EMI',
    title: 'EMI on Bulk Rice',
    desc: 'Order 50kg or more and pay in easy 3 or 6 month installments at just 7% interest.',
    color: '#fef3c7',
    iconBg: '#f59e0b',
  },
  {
    icon: '60D',
    title: '60-Day Replacement',
    desc: 'Received damaged rice? We replace it free of cost within 60 days.',
    color: '#dbeafe',
    iconBg: '#3b82f6',
  },
  {
    icon: 'Best',
    title: 'Farm Best Quality',
    desc: 'Rice packed and shipped directly from verified farmer storage to your doorstep.',
    color: '#d8f3dc',
    iconBg: '#2d6a4f',
  },
];

export default function RiceStoreHighlight() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [meterWidth, setMeterWidth] = useState(0);
  const { data: statsData } = useApi(statsAPI.getGlobal, { summary: 'true' }, { staleTime: 60000 });
  const { data: productsData } = useApi(productAPI.getAll, null, { staleTime: 45000 });
  const { data: farmersData } = useApi(farmerAPI.getPublic, null, { staleTime: 45000 });

  const {
    riceStats,
    totalRiceStock,
    riceCapacity,
    remainingRiceStock,
    meterTargetWidth,
    stockUsageLabel,
  } = useMemo(() => {
    const stats = statsData || {};
    const products = Array.isArray(productsData) ? productsData : [];
    const farmers = Array.isArray(farmersData) ? farmersData : [];
    const riceProducts = products.filter((product) => isRiceProduct(product));
    const totalRiceStock = riceProducts.reduce((sum, product) => sum + Number(product?.availableQuantityKg || 0), 0);
    const uniqueRiceFarmers = new Set(
      riceProducts
        .map((product) => product?.farmerId?._id || product?.farmerId)
        .filter(Boolean)
    ).size;
    const verifiedFarmers = Number(stats.verifiedFarmers ?? farmers.length ?? uniqueRiceFarmers);
    const ordersOrCustomers = Number(stats.totalCustomers || stats.totalOrders || 0);
    const deliveredOrders = Number(stats.deliveredOrders || 0);
    const riceCapacity = totalRiceStock > 0 ? Math.max(Math.ceil(totalRiceStock * 1.25), totalRiceStock) : 0;
    const remainingRiceStock = Math.max(0, riceCapacity - totalRiceStock);
    const meterTargetWidth = riceCapacity > 0 ? Math.round((totalRiceStock / riceCapacity) * 100) : 0;

    return {
      riceStats: [
        { label: 'Live Rice Stock', value: totalRiceStock, suffix: ' kg', icon: 'Rice' },
        { label: stats.totalCustomers ? 'Happy Customers' : 'Total Orders', value: ordersOrCustomers, suffix: '+', icon: 'Users' },
        { label: 'Farmers Connected', value: verifiedFarmers, suffix: '+', icon: 'Farmers' },
        { label: 'Delivered Orders', value: deliveredOrders, suffix: '+', icon: 'Orders' },
      ],
      totalRiceStock,
      riceCapacity,
      remainingRiceStock,
      meterTargetWidth,
      stockUsageLabel: `${meterTargetWidth}% stocked`,
    };
  }, [farmersData, productsData, statsData]);

  useEffect(() => {
    const timer = setTimeout(() => setMeterWidth(meterTargetWidth), 500);
    return () => clearTimeout(timer);
  }, [meterTargetWidth]);

  return (
    <section style={{ backgroundColor: '#fefae0', padding: isMobile ? '56px 16px' : '80px 24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '15px' }}>
            Ready to experience farm-best rice with EMI convenience?
          </p>
          <button
            onClick={() => navigate('/rice-store')}
            style={{
              background: 'linear-gradient(135deg, #2d6a4f, #52b788)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '14px 20px' : '18px 48px',
              borderRadius: '999px',
              fontWeight: '700',
              fontSize: isMobile ? '1rem' : '1.1rem',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(45,106,79,0.35)',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '340px' : 'none',
            }}
          >
            Explore Rice Store
          </button>
        </div>

        <div style={{ textAlign: 'center', margin: isMobile ? '34px 0' : '56px' }}>
          <span
            style={{
              background: '#d8f3dc',
              color: '#2d6a4f',
              padding: '6px 20px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Rice Store Feature
          </span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '700', color: '#1a1a2e', marginTop: '16px' }}>
            Why Buy Rice from Sedyam?
          </h2>
          <p style={{ color: '#6b7280', fontSize: '1.05rem', maxWidth: '600px', margin: '12px auto 0' }}>
            A smarter way to buy rice in bulk directly from farmers, with EMI options and a 60-day replacement guarantee.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 220 : 240}px, 1fr))`,
            gap: isMobile ? '14px' : '24px',
            marginBottom: isMobile ? '34px' : '64px',
          }}
        >
          {concepts.map((c) => (
            <div
              key={c.title}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: isMobile ? '18px' : '28px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                borderTop: `4px solid ${c.iconBg}`,
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: c.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                {c.icon}
              </div>
              <h3 style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1a1a2e', marginBottom: '10px' }}>{c.title}</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.7' }}>{c.desc}</p>
            </div>
          ))}
        </div>

        <div
          style={{
            background: 'linear-gradient(135deg, #1b4332, #2d6a4f)',
            borderRadius: '28px',
            padding: isMobile ? '22px 14px' : '48px 32px',
            marginBottom: isMobile ? '24px' : '48px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '18px' : '40px' }}>
            <h3 style={{ fontSize: isMobile ? '1.35rem' : '1.8rem', fontWeight: '700', color: 'white', marginBottom: '8px' }}>
              Live Impact Meter
            </h3>
            <p style={{ color: '#95d5b2', fontSize: '15px' }}>Real numbers from our farming community</p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 145 : 200}px, 1fr))`,
              gap: isMobile ? '12px' : '24px',
              marginBottom: isMobile ? '18px' : '40px',
            }}
          >
            {riceStats.map((stat) => (
              <div
                key={stat.label}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  padding: isMobile ? '14px' : '24px',
                  textAlign: 'center',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '8px', color: '#d9f99d', fontWeight: 700 }}>{stat.icon}</div>
                <div style={{ fontSize: isMobile ? '1.35rem' : '2rem', fontWeight: '700', color: 'white', marginBottom: '6px' }}>
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <div style={{ color: '#95d5b2', fontSize: '13px', fontWeight: '500' }}>{stat.label}</div>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: isMobile ? '14px' : '24px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : 0,
              }}
            >
              <span style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>Rice Live Stock</span>
              <span style={{ color: '#95d5b2', fontWeight: '700' }}>{totalRiceStock.toLocaleString()} / {riceCapacity.toLocaleString()} kg</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '999px', height: '16px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${meterWidth}%`,
                  background: 'linear-gradient(90deg, #52b788, #95d5b2)',
                  borderRadius: '999px',
                  transition: 'width 2s ease-in-out',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '6px' : 0,
              }}
            >
              <span style={{ color: '#95d5b2', fontSize: '13px' }}>{stockUsageLabel}</span>
              <span style={{ color: '#95d5b2', fontSize: '13px' }}>{remainingRiceStock.toLocaleString()} kgs sold-out</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
